import { Option } from "@inrev/core";
import { type ReactNode, useState } from "react";
import { cn } from "../../lib/utils";
import { Checkbox, ClickableIcon, Text } from "../../primitives";

const suffixId = (base: Option<string>, suffix: string): string | undefined =>
	Option.mapOrElse(base, (id) => `${id}-${suffix}`, undefined);

export type PeriodYearGroup<TItem> = {
	yearKey: string;
	items: readonly TItem[];
};

export type PeriodYearListProps<TItem> = {
	groups: readonly PeriodYearGroup<TItem>[];
	getItemKey: (item: TItem) => string;
	isSelected: (item: TItem) => boolean;
	onToggle: (item: TItem) => void;
	/** The row's content beside the checkbox. Clicking it toggles the checkbox. */
	renderLabel: (item: TItem) => ReactNode;
	emptyLabel?: string;
	testId?: string;
	trackingId?: string;
	className?: string;
};

export const PeriodYearList = <TItem,>({
	groups,
	getItemKey,
	isSelected,
	onToggle,
	renderLabel,
	emptyLabel = "No periods available",
	testId,
	trackingId,
	className,
}: PeriodYearListProps<TItem>) => {
	const [collapsedYears, setCollapsedYears] = useState<Set<string>>(() => new Set());

	const toggleYear = (yearKey: string) => {
		setCollapsedYears((prev) => {
			const next = new Set(prev);
			if (next.has(yearKey)) next.delete(yearKey);
			else next.add(yearKey);
			return next;
		});
	};

	if (groups.length === 0) {
		return (
			<div className={className}>
				<Text
					type="body"
					color="tertiary"
					className="italic"
					data-testid={suffixId(testId, "empty")}
				>
					{emptyLabel}
				</Text>
			</div>
		);
	}

	return (
		<div className={cn("space-y-3", className)}>
			{groups.map(({ yearKey, items }) => {
				const isYearCollapsed = collapsedYears.has(yearKey);
				return (
					<div
						key={yearKey}
						className="space-y-1"
						data-testid={suffixId(testId, `year-${yearKey}`)}
					>
						<div className="flex select-none items-center gap-1.5">
							<ClickableIcon
								type={isYearCollapsed ? "chevron-right" : "chevron-down"}
								size="micro"
								color="tertiary"
								onClick={() => toggleYear(yearKey)}
								testId={suffixId(testId, `year-toggle-${yearKey}`)}
								trackingId={suffixId(trackingId, `year-toggle-${yearKey}`)}
								aria-expanded={!isYearCollapsed}
								aria-label={`${isYearCollapsed ? "Expand" : "Collapse"} ${yearKey}`}
							/>
							<Text type="eyebrow" color="secondary">
								{yearKey}
							</Text>
						</div>
						{!isYearCollapsed && (
							<div className="ml-[5.5px] space-y-0.5 border-l border-bc-default pl-2">
								{items.map((item) => {
									const itemKey = getItemKey(item);
									return (
										<div
											key={itemKey}
											data-testid={suffixId(testId, `row-${itemKey}`)}
											className="rounded-lg p-2 transition-colors hover:bg-surface-light"
										>
											<Checkbox
												checked={isSelected(item)}
												onChange={() => onToggle(item)}
												data-testid={suffixId(testId, `checkbox-${itemKey}`)}
												trackingId={suffixId(trackingId, `checkbox-${itemKey}`)}
												label={
													<div className="flex min-w-0 flex-1 flex-col gap-2.5 pl-2">
														{renderLabel(item)}
													</div>
												}
											/>
										</div>
									);
								})}
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
};
